import axios from "axios"
import { cookies } from "next/headers"
import { NextResponse } from "next/server"
import { z } from "zod"
import { clearSession, getRequiredSessionCookie } from "@/services/auth-service"
import {
  applyWooCartSessionCookies,
  buildForwardCookieHeader,
  readWooGuestSessionCookies,
} from "@/services/cart-service"
import { submitCheckoutOrder } from "@/services/checkout-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const KNOWN_CHECKOUT_ERROR_CODES = new Set([
  "CHECKOUT_REJECTED",
  "CHECKOUT_SUBMIT_FAILED",
  "CHECKOUT_CONTEXT_UNAVAILABLE",
])

const checkoutSubmitBasePayloadSchema = z.object({
  shipToDifferentAddress: z.boolean(),
  billing: z.object({
    firstName: z.string().trim().min(1),
    lastName: z.string().trim().min(1),
    email: z.email(),
    phone: z.string().trim().min(1),
    cpf: z.string().trim().min(1),
    country: z.string().trim().min(1),
    state: z.string().trim().min(1),
    city: z.string().trim().min(1),
    postcode: z.string().trim().min(1),
    address1: z.string().trim().min(1),
    address2: z.string().trim().optional(),
    number: z.string().trim().optional(),
    neighborhood: z.string().trim().optional(),
  }),
  shipping: z
    .object({
      firstName: z.string().trim().min(1),
      lastName: z.string().trim().min(1),
      country: z.string().trim().min(1),
      state: z.string().trim().min(1),
      city: z.string().trim().min(1),
      postcode: z.string().trim().min(1),
      address1: z.string().trim().min(1),
      address2: z.string().trim().optional(),
      number: z.string().trim().optional(),
      neighborhood: z.string().trim().optional(),
    })
    .optional(),
  orderNotes: z.string().trim().optional(),
  termsAccepted: z.boolean().optional(),
})

const checkoutCardSchema = z.object({
  holderName: z.string().trim().min(1),
  holderCpf: z.string().trim().min(1),
  holderPhone: z.string().trim().min(1),
  number: z.string().trim().min(1),
  expiry: z.string().trim().min(1),
  cvc: z.string().trim().min(1),
  brand: z.string().trim().min(1),
  installment: z.string().trim().min(1),
})

const checkoutSubmitPayloadSchema = z
  .discriminatedUnion("paymentMethod", [
    checkoutSubmitBasePayloadSchema.extend({
      paymentMethod: z.literal("bacs"),
    }),
    checkoutSubmitBasePayloadSchema.extend({
      paymentMethod: z.literal("triibo-payments-pix"),
    }),
    checkoutSubmitBasePayloadSchema.extend({
      paymentMethod: z.literal("triibo-payments-card"),
      card: checkoutCardSchema,
    }),
  ])
  .refine(
    (payload) => {
      if (!payload.shipToDifferentAddress) {
        return true
      }

      return Boolean(payload.shipping)
    },
    {
      message:
        "Shipping address is required when shipToDifferentAddress is true.",
      path: ["shipping"],
    }
  )

const parsePayload = async (request: Request): Promise<unknown> => {
  const rawBody = await request.text()

  if (!rawBody.trim()) {
    return {}
  }

  try {
    return JSON.parse(rawBody) as unknown
  } catch {
    return null
  }
}

const getForwardedCookieHeader = async (
  authSessionToken: string
): Promise<string> => {
  const cookieStore = await cookies()

  return buildForwardCookieHeader({
    authSessionToken,
    wooGuestSessionCookies: readWooGuestSessionCookies(cookieStore),
  })
}

const toResultStatus = (
  result: Awaited<ReturnType<typeof submitCheckoutOrder>>
): number => {
  if (result.ok) {
    return 200
  }

  if (result.code === "CHECKOUT_REJECTED") {
    return 400
  }

  return 502
}

const toCheckoutErrorCode = (error: unknown): string => {
  if (error instanceof Error && KNOWN_CHECKOUT_ERROR_CODES.has(error.message)) {
    return error.message
  }

  return "CHECKOUT_SUBMIT_FAILED"
}

const isUnauthorizedAxiosError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false
  }

  const status = error.response?.status

  return status === 401 || status === 403
}

export const POST = async (request: Request) => {
  const payload = await parsePayload(request)
  const parsedPayload = checkoutSubmitPayloadSchema.safeParse(payload)

  if (!parsedPayload.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para finalizar compra.",
      },
      {
        status: 422,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  const sessionCookie = await getRequiredSessionCookie()

  if (!sessionCookie) {
    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Sessao nao autenticada.",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  try {
    const forwardedCookieHeader = await getForwardedCookieHeader(sessionCookie)
    const result = await submitCheckoutOrder(
      forwardedCookieHeader,
      parsedPayload.data
    )
    const response = NextResponse.json(
      {
        ok: result.ok,
        code: result.code,
        message: result.message,
        redirectUrl: result.redirectUrl,
      },
      {
        status: toResultStatus(result),
        headers: NO_CACHE_HEADERS,
      }
    )

    applyWooCartSessionCookies(response, result.upstreamSetCookie)

    return response
  } catch (error) {
    if (isUnauthorizedAxiosError(error)) {
      await clearSession()

      return NextResponse.json(
        {
          ok: false,
          code: "UNAUTHORIZED",
          message: "Sessao nao autenticada.",
        },
        {
          status: 401,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    return NextResponse.json(
      {
        ok: false,
        code: toCheckoutErrorCode(error),
        message: "Nao foi possivel finalizar compra.",
      },
      {
        status: 502,
        headers: NO_CACHE_HEADERS,
      }
    )
  }
}
